// §6.3 — the Windows 11 window frame: mica, an 8px radius, and `─ □ ✕` on the right.
//
// The drag and resize path is the kernel's (`kernel/wm/drag`), exactly as the house frame's is:
// geometry is written to the DOM and committed once (§5.2). A skin decides what a frame *looks*
// like and never how a window moves.
//
// This file deliberately does not import anything from `skins/house` — it may not (§6.5), and the
// duplication is the point: the second implementation is what proves the seam is real.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Edge, Win } from '@hangar/shared';
import { Minus, Square, Copy, X } from '@/assets/icons';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import { MOVE, RESIZE_EDGES, beginDrag, resizeFrom, type DragSession } from '@/kernel/wm/drag';
import { windowsPolicy } from '../policy';
import { WindowMenuBar } from '../../menubar';
import { SnapPicker } from './SnapPicker';

/** §5.4 — how long the maximize button must be hovered before Snap Layouts opens. */
const PICKER_OPEN_DELAY_MS = 320;
/** …and how long the pointer may be off both the button and the flyout before it closes again. */
const PICKER_CLOSE_GRACE_MS = 260;

export function WindowFrame({ win }: { win: Win }) {
  const kernel = useKernel();
  const session = useRef<DragSession | null>(null);
  // §5.4 — Snap Layouts. Hover-to-open with a delay, so passing over the button on the way to ✕
  // does not throw a flyout at you; hover-out has its own grace so the pointer can travel to it.
  // **One timer for both the button and the flyout**, or moving between them closes it.
  const [picker, setPicker] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHover = useCallback(() => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }, []);
  useEffect(() => clearHover, [clearHover]);

  const closePicker = useCallback(() => {
    clearHover();
    setPicker(false);
  }, [clearHover]);

  const releasePicker = useCallback(() => {
    clearHover();
    hoverTimer.current = setTimeout(() => setPicker(false), PICKER_CLOSE_GRACE_MS);
  }, [clearHover]);

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, edge: Edge | null) => {
      if (event.button !== 0) return;
      const host = event.currentTarget.closest<HTMLElement>('.win-host');
      if (!host) return;

      const current = kernel.wm.get(win.id);
      if (!current) return;
      if (edge !== null && !current.resizable) return;

      kernel.wm.focus(win.id);
      event.preventDefault();

      const kind = edge === null ? MOVE : resizeFrom(edge);
      // §5.4 — only a move can snap; a resize is the user saying what size they want.
      if (edge === null) kernel.snap.begin(win.id);
      session.current = beginDrag({
        element: host,
        capture: event.currentTarget,
        start: current.rect,
        origin: { clientX: event.clientX, clientY: event.clientY },
        pointerId: event.pointerId,
        kind,
        min: { w: current.minW, h: current.minH },
        // §5.2 (D-438) — passed for a resize as well as a move. A move is held wholly inside the
        // work area; a resize only has its top and left edges held there, so a window can still be
        // made larger than the screen without its title bar ending up above it.
        bounds: kernel.workArea(),
        onCommit: (rect) => {
          session.current = null;
          if (edge !== null) {
            kernel.wm.resize(win.id, rect);
            return;
          }
          if (!kernel.snap.commit()) kernel.wm.setRect(win.id, rect);
        },
        onCancel: () => kernel.snap.cancel(),
      });
    },
    [kernel, win.id],
  );

  const onPointerMove = (event: React.PointerEvent) => {
    if (!session.current) return;
    session.current.pointerMove(event.nativeEvent);
    kernel.snap.update({ x: event.clientX, y: event.clientY });
  };
  const onPointerUp = (event: React.PointerEvent) => session.current?.pointerUp(event.nativeEvent);
  const onPointerCancel = () => {
    kernel.snap.cancel();
    session.current?.cancel();
  };

  const onMaximize = () => {
    const current = kernel.wm.get(win.id);
    if (!current) return;
    if (current.state === 'maximized' || current.state === 'snapped') kernel.wm.restore(win.id);
    // §5.4 — the Windows skin's maximize button maximizes; the work area already excludes the
    // 48px taskbar, so a maximized window stops above it.
    else if (windowsPolicy.onMaximizeButton(current) === 'maximize') {
      kernel.wm.maximize(win.id, kernel.workArea());
    }
  };

  const restoring = win.state === 'maximized' || win.state === 'snapped';
  // A window that cannot be resized cannot be put in a zone either.
  const snappable = win.resizable && kernel.policy().snapZones(kernel.workArea()).length > 0;

  return (
    <div className="win11-frame" data-testid={`window-frame-${win.id}`}>
      <div
        className="win11-titlebar"
        data-testid={`titlebar-${win.id}`}
        onPointerDown={(e) => startDrag(e, null)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onMaximize}
      >
        {/* Generated from the app id (D-043), or the app's own icon already wrapped in a
            data-URI `<img>` by the api host — never third-party markup (D-122). */}
        <span
          className="win11-title-icon"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: kernel.windowIconSvg(win) }}
        />
        <span className="win11-title" title={win.title}>
          {win.title}
        </span>
        {win.dirty ? (
          <span className="win11-dirty" data-testid="window-dirty" aria-label={COPY.chrome.unsaved} />
        ) : null}
        {win.busy ? (
          <span className="win11-busy" data-testid="window-busy" aria-label={COPY.chrome.busy} />
        ) : null}

        {/* §6.3 — `─ □ ✕`, in that order, on the right. */}
        <div className="win11-controls" data-testid={`window-controls-${win.id}`}>
          <button
            type="button"
            className="win11-control"
            aria-label={COPY.chrome.minimize}
            data-glyph="minimize"
            data-testid={`window-minimize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => kernel.wm.minimize(win.id)}
          >
            <Minus size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="win11-control"
            aria-label={restoring ? COPY.chrome.restore : COPY.chrome.maximize}
            data-glyph="maximize"
            data-testid={`window-maximize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              closePicker();
              onMaximize();
            }}
            onPointerEnter={() => {
              if (!snappable) return;
              clearHover();
              hoverTimer.current = setTimeout(() => setPicker(true), PICKER_OPEN_DELAY_MS);
            }}
            onPointerLeave={releasePicker}
          >
            {restoring ? <Copy size={12} aria-hidden /> : <Square size={11} aria-hidden />}
          </button>
          <button
            type="button"
            className="win11-control win11-control--close"
            aria-label={COPY.chrome.close}
            data-glyph="close"
            data-testid={`window-close-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            // §18 F9 — the user asking is what makes this vetoable; `wm.close` is not.
            onClick={() => void kernel.requestClose(win.id)}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
        {picker ? (
          <SnapPicker
            winId={win.id}
            layouts={kernel.policy().snapZones(kernel.workArea())}
            onHold={clearHover}
            onRelease={releasePicker}
            onChosen={closePicker}
          />
        ) : null}
      </div>

      {/* §6.3 — in-window menu bar, under the title bar. Draws nothing until the app publishes. */}
      <WindowMenuBar win={win} />

      {win.resizable && win.state === 'normal' ? (
        <div className="win-resize-handles" aria-hidden>
          {RESIZE_EDGES.map((edge) => (
            <div
              key={edge}
              className="win-resize-handle"
              data-edge={edge}
              data-testid={`resize-${edge}-${win.id}`}
              onPointerDown={(e) => startDrag(e, edge)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
